import { computed, ComputedRef, InjectionKey, Ref, shallowRef } from "vue";
import { asyncComputed, injectLocal, provideLocal } from "@vueuse/core";
import { addBreadcrumb, captureException, getClient } from "@sentry/browser";

import {
  Link,
  Links,
  Locator,
  Manifest,
  Metadata,
  Publication,
  ReadingProgression,
} from "@readium/shared";

import { parseToken, Token } from "./token";
import { AuthType, ReadingSession } from "./session";

import OfflineReading, {
  OfflineMetadata,
} from "../services/offline/OfflineService";
import { OfflineError, RequestTimeoutError, UnauthorizedError } from "./errors";
import { BASE, MODE } from "@/consts";
import Device from "./device";
import { deserializeSession, serializeSession } from "./helpers";
import { makeOfflineChecksum } from "@/helpers/drm";
import { invokeAsync } from "@/helpers/async";
import { WPSClientFetcher } from "./fetcher";
import { Media } from "./media";
import { assertJson, assertOk } from "./responses";

import fetch, {
  shouldRetryJsonFailure,
  RetryOptions,
} from "../../modules/shared-utils/fetch";

/**
 * Decorate an async method to ensure it is only executed once at a time.
 */
function singleFlight<T extends (...args: unknown[]) => Promise<unknown>>(
  _target: unknown,
  property: string | symbol,
  descriptor: TypedPropertyDescriptor<T>
): TypedPropertyDescriptor<T> {
  const key = Symbol(`singleFlight:${String(property)}`) as PropertyKey;
  const originalMethod = descriptor.value as T;
  return {
    ...descriptor,
    value: function (...args: Parameters<T>): ReturnType<T> {
      if (this[key]) {
        return this[key] as ReturnType<T>;
      }
      // eslint-disable-next-line no-async-promise-executor
      this[key] = new Promise(async (resolve, reject) => {
        try {
          resolve(await originalMethod.apply(this, args));
        } catch (error) {
          reject(error);
        } finally {
          // Clear the key after the promise resolves or rejects
          delete this[key];
        }
      });
      return this[key] as ReturnType<T>;
    } as unknown as T,
  };
}

function computeAuthType(type: AuthType, token: string): AuthType {
  if (type !== "read") {
    return type;
  }

  if (MODE !== "production" && !token.includes(".")) {
    return "test";
  }

  if (window.location.hostname === "localhost") {
    // "gs://[[LOCAL]]/"
    if (token.startsWith("Z3M6Ly9bW0xPQ0FMXV0v")) {
      return "test";
    }

    // "gs://reader-publication-sandbox-staging-northamerica/test-publications"
    if (
      token.startsWith(
        "Z3M6Ly9yZWFkZXItcHVibGljYXRpb24tc2FuZGJveC1zdGFnaW5nLW5vcnRoYW1lcmljYS90ZXN0LXB1YmxpY2F0aW9ucy"
      )
    ) {
      return "test";
    }
  }

  return type;
}

export class WPSClient extends EventTarget {
  private readonly type: AuthType;
  public readonly token: Token;
  public readonly media: Media;

  private _session: Ref<ReadingSession | null> = shallowRef(null);
  private _manifest: Ref<Manifest | null> = shallowRef(null);
  private _identifier: Ref<string | null> = shallowRef(null);
  private drmIdentifier: string | null = null;
  private _publication: ComputedRef<Publication | null> = computed(() => {
    if (!this._manifest.value) {
      return null;
    }

    return new Publication({
      manifest: this._manifest.value,
      fetcher: new WPSClientFetcher(this),
    });
  });

  private readonly _positions: Ref<Locator[]>;

  public get identifier(): string | null {
    return this._identifier.value || null;
  }

  public get publication(): Publication | null {
    return this._publication.value || null;
  }

  public get positions(): Locator[] {
    return this._positions.value || [];
  }

  public get canonicalUrl(): Link | null {
    return this.publication?.links.findWithRel("canonical") || null;
  }

  public get downloadLink(): Link | null {
    return this.publication?.links.findWithRel("preview") || null;
  }

  public get interactive(): boolean {
    // Disable interactive mode so it doesn't interfere with the media overlay
    if (this.media.isMediaOverlay) {
      return false;
    }

    if (this.token.interactiveMode) {
      return true;
    }
    if (this.media.isFrog) {
      // Frog publications have their own UI elements
      return true;
    }
    if (!this._publication.value) {
      return false;
    }

    const manifest = this._publication.value.manifest;
    const jsInReadingOrder = manifest.readingOrder.items.some((item) =>
      item.properties?.getContains()?.has("js")
    );

    if (jsInReadingOrder) {
      return true;
    }

    return !!manifest.resources?.findWithMediaType("text/javascript");
  }

  constructor(
    type: AuthType,
    token: string,
    private readonly ssrManifest: Manifest | null = null,
    positions: Locator[] | null = null
  ) {
    super();
    type = computeAuthType(type, token);
    this.type = type;
    this.token = parseToken(type, token);
    this.media = new Media(this._manifest);

    this._positions = asyncComputed<Locator[]>(async () => {
      if (!this.publication) {
        return [];
      }

      if (this.media.isFXL) {
        // Way of dealing with inaccurate fixed-layout positions list is to make our own
        const total = this.publication.readingOrder.items.length;
        return this.publication.readingOrder.items.map((l, i) =>
          Locator.deserialize({
            href: l.href,
            type: l.type,
            locations: {
              position: i + 1,
              progression: 0,
              totalProgression: (i + 1) / total,
            },
          })
        );
      }

      return await this.publication.positionsFromManifest();
    }, positions || []);

    invokeAsync(async () => {
      await this.session();
      await this.manifest();
    });
  }

  @singleFlight
  public async session(refresh = false): Promise<ReadingSession> {
    if (this._session.value && !refresh) {
      return this._session.value;
    }

    if (!navigator.onLine) {
      addBreadcrumb({
        category: "wps",
        message: "Browser says navigator.onLine is false",
      });
    }

    // ignore navigator.onLine as it's unreliable in some cases
    // see: https://issues.chromium.org/issues/466796612
    const session = await this.onlineSession();

    if (!session.ok) {
      throw new Error("Session expired");
    }

    return (this._session.value = session);
  }

  private async onlineSession(): Promise<ReadingSession> {
    const params = {
      type: this.type,
      token: this.token.raw,
      device: await Device.id,
    };
    const extra = { ...params, token: null, input: params.token };
    let response: Response | null = null;
    try {
      response = await fetch(`${BASE}/session`, {
        method: "POST",
        body: new URLSearchParams(params),
        shouldRetry: shouldRetryJsonFailure,
      });
    } catch (error) {
      // possibly network error
      // try offline
      captureException(error, { extra });
      return await this.offlineSession();
    }

    const sessionData = await assertJson<unknown>(response, extra);
    const session = deserializeSession(sessionData)!;

    if (session.ok && (await OfflineReading.check(session.path))) {
      await this.persistOfflineSession(session);
    }

    return session;
  }

  private async offlineSession(): Promise<ReadingSession> {
    await OfflineReading.init;

    const path = OfflineReading.lookup(this.token.raw);
    if (!path) {
      throw new OfflineError(
        `navigator is offline with no session found for token`,
        { type: this.type, token: this.token }
      );
    }

    const session = await OfflineReading.check(path);
    if (session) {
      return session;
    }

    throw new OfflineError(
      `navigator is offline with session check failed for token`,
      { type: this.type, token: this.token }
    );
  }

  private async persistOfflineSession(session: ReadingSession): Promise<void> {
    const meta: OfflineMetadata = {
      init: {
        // TODO: raw device ID appears to not be used by service worker
        device: await Device.rawId(),
        token: this.token.raw,
        type: this.type,
      },
      session: serializeSession(session),
      finalized: true,
      checksum: await makeOfflineChecksum(
        `${session.id}|${this.type}|${session.refresh?.token || this.token}`
      ),
    };

    // store the session in the service worker
    await OfflineReading.store(meta);
  }

  @singleFlight
  public async manifest(): Promise<Manifest> {
    if (this._manifest.value) {
      return this._manifest.value;
    }

    const session = await this.session();

    const manifest = this.ssrManifest
      ? this.ssrManifest
      : await (async () => {
          const response = await this.fetchResource(
            "/manifest.json",
            {
              shouldRetry: shouldRetryJsonFailure,
            },
            false
          );
          const data = await assertJson<unknown>(response);
          return Manifest.deserialize(data)!;
        })();

    // this needs to be the original identifier to match DRM key from the WPS
    const identifier = (this.drmIdentifier = manifest.metadata.identifier);
    await OfflineReading.current(session.path, identifier);

    // ensure we have an identifier for the publication store
    this._identifier.value = identifier || session.id;

    // patch manifest with session data
    overrideManifest(manifest, session);

    return (this._manifest.value = manifest);
  }

  public async fetchResource(
    input: RequestInfo | URL,
    init?: (RequestInit & RetryOptions) | undefined,
    // encrypted resources need to be fetched through the service worker
    // to ensure the DRM can decode them
    requireSWCtrl = true
  ): Promise<Response> {
    const session = await this.session();
    let request: Request | undefined;
    if (typeof input === "string" || input instanceof URL) {
      const url = new URL(input, window.location.origin);
      url.pathname = `${BASE}/${session.path}${url.pathname}`;
      request = new Request(url);
    } else {
      const url = new URL(input.url, window.location.origin);
      url.pathname = `${BASE}/${session.path}${url.pathname}`;
      request = new Request(url, input);
    }

    // patch to allow SW to decode DRM when state isn't persisted yet
    request.headers.set("X-Publication-ID", this.drmIdentifier || "");

    // device id for per user DRM
    request.headers.set("X-Device-ID", await Device.id);

    // add release info
    const release = getClient()?.getOptions()?.release;
    if (release) {
      request.headers.set("X-Reader-Release", release);
    }

    if (requireSWCtrl) {
      await OfflineReading.worker.untilControlledBy();
    }

    const response = await fetch(request, init);
    return assertOk(response, { url: request.url, method: request.method });
  }
}

const overrideKeys: Exclude<keyof Metadata, "effectiveReadingProgression">[] = [
  "title",
  "subtitle",
  "authors",
  "languages",
  "description",
  "publishers",
  "modified",
];

function overrideManifest(manifest: Manifest, session: ReadingSession): void {
  const proxy_url = new URL(BASE, window.location.origin);
  const base_url = new URL(manifest.baseURL);
  // somehow, on session refresh, we re-apply the base URL
  if (base_url.pathname.startsWith(proxy_url.pathname)) {
    proxy_url.pathname = base_url.pathname;
  } else {
    proxy_url.pathname += base_url.pathname;
  }
  manifest.setSelfLink(proxy_url.toString());

  if (session.id) {
    manifest.metadata.identifier = session.id;
  }
  const sessionMeta = session.content.metadata;
  for (const key of overrideKeys) {
    const value = sessionMeta?.[key] as unknown;
    if (value) {
      (manifest.metadata[key] as unknown) = value;
    }
  }
  if (
    sessionMeta?.readingProgression &&
    sessionMeta.readingProgression !== ReadingProgression.auto
  ) {
    manifest.metadata.readingProgression = sessionMeta.readingProgression;
  }
  const cover = session.content.resources?.find((r) => r.rels?.has("cover"));
  if (cover) {
    replaceLink(manifest.links, cover);
  }
  session.content.links?.forEach((link) => replaceLink(manifest.links, link));
}

function replaceLink(links: Links, link: Link): void {
  links.items = links.items.filter((l) => !l.rels?.has(link.rels[0]));
  links.items.push(link);
}

function initSsrClient(): WPSClient | null {
  const ssrManifest = document.getElementById("ssr-manifest");
  if (!ssrManifest) {
    return null;
  }
  const manifestData = JSON.parse(ssrManifest.textContent);
  const manifest = Manifest.deserialize(manifestData);

  const ssrPositions = document.getElementById("ssr-positions");
  let positions: Locator[] = null;
  if (ssrPositions) {
    const positionsData = JSON.parse(ssrPositions.textContent);
    positions = positionsData.map((p: unknown) => Locator.deserialize(p));
  }

  return new WPSClient(
    ssrManifest.dataset.type as AuthType,
    ssrManifest.dataset.path,
    manifest,
    positions
  );
}

// TODO: maybe use WeakRef eventually?
const clients = new Map<string, WPSClient>();

try {
  const ssrClient = initSsrClient();
  if (ssrClient) {
    clients.set(ssrClient.token.raw, ssrClient);
  }
} catch (e) {
  captureException(e);
}

export function newForToken(type: AuthType, token: string): WPSClient {
  if (clients.has(token)) {
    return clients.get(token)!;
  }

  const client = new WPSClient(type, token);
  clients.set(token, client);
  return client;
}

const wpsClientKey = Symbol("wpsClient") as InjectionKey<WPSClient>;

export function provideWpsClient(client: WPSClient): void {
  provideLocal(wpsClientKey, client);
}

export function useWpsClient(): WPSClient {
  const client = injectLocal(wpsClientKey);
  if (!client) {
    throw new Error("useWpsClient must be used within a provideWpsClient");
  }
  return client;
}
